import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Bot, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Quais ERPs vocês atendem?",
  "Como funciona a preparação para a Reforma Tributária?",
  "O que é a trilha Academy?",
  "Como agendar um diagnóstico?",
];

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Olá! Sou a assistente virtual da Ponzena. Como posso ajudar você hoje? Posso falar sobre nossos serviços, ERPs, trilha Academy ou agendar um diagnóstico.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, open, loading]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId || "",
        message: text,
      });
      if (!sessionId) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: "Desculpe, tive um problema para responder. Você pode tentar novamente ou falar direto pelo WhatsApp (11) 98033-1231.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setOpen(!open)}
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full flex items-center justify-center transition-all pulse-glow ${
          open
            ? "bg-[#0A192F] border border-cyan-400/40"
            : "bg-gradient-to-br from-cyan-400 to-blue-500"
        }`}
        aria-label="Chat com IA"
        data-testid="chatbot-toggle"
      >
        {open ? (
          <X size={22} className="text-cyan-400" />
        ) : (
          <Bot size={26} className="text-[#030B1E]" />
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[400px] max-h-[600px] card-surface flex flex-col shadow-[0_20px_60px_rgba(0,229,255,0.35)]"
          data-testid="chatbot-panel"
        >
          <div className="px-5 py-4 border-b border-cyan-400/15 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center">
              <Sparkles size={18} className="text-cyan-400" />
            </div>
            <div>
              <p className="font-display font-semibold text-white leading-tight">Assistente Ponzena</p>
              <p className="text-[10px] text-cyan-400 font-mono-tech tracking-widest uppercase">
                // ONLINE · IA
              </p>
            </div>
          </div>

          <div
            ref={bodyRef}
            className="flex-1 overflow-y-auto p-5 space-y-4 min-h-[280px] max-h-[380px]"
            data-testid="chatbot-messages"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-cyan-400 text-[#030B1E] rounded-br-sm font-medium"
                      : "bg-[#030B1E] border border-cyan-400/20 text-[#D8DDE8] rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#030B1E] border border-cyan-400/20 rounded-2xl px-4 py-3 flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 animate-bounce" />
                  <span className="w-2 h-2 rounded-full bg-cyan-400 animate-bounce [animation-delay:120ms]" />
                  <span className="w-2 h-2 rounded-full bg-cyan-400 animate-bounce [animation-delay:240ms]" />
                </div>
              </div>
            )}
          </div>

          {messages.length <= 1 && (
            <div className="px-5 pb-2 flex flex-wrap gap-2" data-testid="chatbot-suggestions">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  className="text-xs text-cyan-400 border border-cyan-400/25 rounded-full px-3 py-1.5 hover:bg-cyan-400/10 transition-colors"
                  data-testid={`chatbot-suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="p-4 border-t border-cyan-400/15 flex gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Digite sua pergunta…"
              className="flex-1 bg-[#030B1E] border border-cyan-400/20 rounded-full px-4 py-2.5 text-sm text-white focus:border-cyan-400 transition-colors"
              data-testid="chatbot-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-11 h-11 rounded-full bg-cyan-400 text-[#030B1E] flex items-center justify-center hover:shadow-[0_0_20px_rgba(0,229,255,0.55)] transition-shadow disabled:opacity-60"
              data-testid="chatbot-send"
              aria-label="Enviar"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
